import * as THREE from 'three';
import { GTAOPass } from 'three/addons/postprocessing/GTAOPass.js';
import { GTAOShader } from 'three/addons/shaders/GTAOShader.js';
import { FullScreenQuad } from 'three/addons/postprocessing/Pass.js';

/** Half/quarter-resolution AO with one normal reconstruction and depth-aware upsampling. */
export class DepthAOPass extends GTAOPass {
  private normalTarget = new THREE.WebGLRenderTarget(1,1,{type:THREE.HalfFloatType,depthBuffer:false,minFilter:THREE.NearestFilter,magFilter:THREE.NearestFilter});
  private reconstruction: THREE.ShaderMaterial;
  private normalQuad: FullScreenQuad;
  constructor(scene: THREE.Scene, camera: THREE.PerspectiveCamera, private resolutionScale: number) {
    super(scene,camera,1,1);
    this.blendIntensity=.65;
    this.updateGtaoMaterial({radius:.45,thickness:.5,distanceFallOff:1,samples:resolutionScale===.5?24:12});
    this.updatePdMaterial({samples:resolutionScale===.5?12:8,radius:resolutionScale===.5?4:2});
    // Reuse the pinned Three shader's discontinuity-aware depth reconstruction,
    // without its AO sampling loop. Denoise can then fetch normals in one tap.
    const main = GTAOShader.fragmentShader.indexOf('void main()');
    if (main<0) throw new Error('GTAO shader interface changed');
    this.reconstruction = new THREE.ShaderMaterial({
      defines:{...GTAOShader.defines,PERSPECTIVE_CAMERA:1,NORMAL_VECTOR_TYPE:0,DEPTH_SWIZZLING:'x'},
      uniforms:this.gtaoMaterial.uniforms, vertexShader:GTAOShader.vertexShader,
      fragmentShader:GTAOShader.fragmentShader.slice(0,main)+`void main() {
        float depth=getDepth(vUv);
        gl_FragColor=vec4(packNormalToRGB(depth>=1.0?vec3(0.,0.,1.):getViewNormal(vUv)),1.);
      }`, depthTest:false,depthWrite:false,
    });
    this.normalQuad=new FullScreenQuad(this.reconstruction);
    Object.assign(this.blendMaterial.uniforms,{
      tDepth:{value:null},lowResolution:{value:new THREE.Vector2(1,1)},cameraNear:{value:camera.near},cameraFar:{value:camera.far},
    });
    this.blendMaterial.fragmentShader=`
      varying vec2 vUv;
      uniform sampler2D tDiffuse;
      uniform sampler2D tDepth;
      uniform vec2 lowResolution;
      uniform float intensity;
      uniform float cameraNear;
      uniform float cameraFar;
      #include <packing>
      void main() {
        float depth=texture2D(tDepth,vUv).x;
        if(depth>=1.) { gl_FragColor=vec4(1.); return; }
        float z=perspectiveDepthToViewZ(depth,cameraNear,cameraFar);
        vec2 pixel=vUv*lowResolution-.5, base=floor(pixel), f=fract(pixel);
        float sum=0., weightSum=0.;
        for(int y=0;y<2;y++) for(int x=0;x<2;x++) {
          vec2 offset=vec2(float(x),float(y));
          vec2 uv=(base+offset+.5)/lowResolution;
          float sampleZ=perspectiveDepthToViewZ(texture2D(tDepth,uv).x,cameraNear,cameraFar);
          vec2 bilinear=mix(1.-f,f,offset);
          float weight=bilinear.x*bilinear.y*exp(-abs(z-sampleZ)*20.);
          sum+=texture2D(tDiffuse,uv).r*weight; weightSum+=weight;
        }
        float ao=weightSum>0.00001?sum/weightSum:1.;
        gl_FragColor=vec4(vec3(mix(1.,ao,intensity)),1.);
      }`;
  }
  override setSize(width: number,height: number): void {
    const w=Math.max(1,Math.round(width*this.resolutionScale)),h=Math.max(1,Math.round(height*this.resolutionScale));
    super.setSize(w,h);this.normalTarget.setSize(w,h);this.blendMaterial.uniforms.lowResolution!.value.set(w,h);
  }
  override render(renderer: THREE.WebGLRenderer,write: THREE.WebGLRenderTarget,read: THREE.WebGLRenderTarget): void {
    this.setGBuffer(read.depthTexture!,this.normalTarget.texture);
    this.gtaoMaterial.uniforms.cameraProjectionMatrixInverse!.value.copy((this.camera as THREE.PerspectiveCamera).projectionMatrixInverse);
    renderer.setRenderTarget(this.normalTarget);this.normalQuad.render(renderer);
    this.blendMaterial.uniforms.tDepth!.value=read.depthTexture;
    this.blendMaterial.uniforms.cameraNear!.value=(this.camera as THREE.PerspectiveCamera).near;
    this.blendMaterial.uniforms.cameraFar!.value=(this.camera as THREE.PerspectiveCamera).far;
    super.render(renderer,write,read,0,false);
  }
  override dispose(): void {
    super.dispose();this.normalTarget.dispose();this.normalQuad.dispose();this.reconstruction.dispose();
    // These materials are omitted by Three r183's GTAOPass.dispose().
    this.gtaoMaterial.dispose();this.blendMaterial.dispose();
  }
}
